import type {
  Client,
  ClientDetail,
  ClientFilter,
  ClientRepository,
  ClientType,
  ExecutionContext,
  SaveClientCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown) => Number(v ?? 0);

function toClient(row: Row): Client {
  return {
    id: row.id,
    type: row.type as ClientType,
    firstName: row.first_name ?? null,
    lastName: row.last_name ?? null,
    companyName: row.company_name ?? null,
    siret: row.siret ?? null,
    email: row.email ?? null,
    phone: row.phone ?? null,
    phoneSecondary: row.phone_secondary ?? null,
    addressLine1: row.address_line1 ?? null,
    postalCode: row.postal_code ?? null,
    city: row.city ?? null,
    notes: row.notes ?? null,
    isPublicEntity: Boolean(row.is_public_entity),
    chorusProStructureId: row.chorus_pro_structure_id ?? null,
    createdAt: row.created_at,
  };
}

function toColumns(command: SaveClientCommand): Row {
  return {
    type: command.type,
    first_name: command.firstName,
    last_name: command.lastName,
    company_name: command.companyName,
    siret: command.siret,
    email: command.email,
    phone: command.phone,
    phone_secondary: command.phoneSecondary,
    address_line1: command.addressLine1,
    postal_code: command.postalCode,
    city: command.city,
    notes: command.notes,
    is_public_entity: command.isPublicEntity,
    chorus_pro_structure_id: command.chorusProStructureId,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseClientRepository(
  clientFor: DbClientFactory,
): ClientRepository {
  return {
    async list(ctx: ExecutionContext, filter?: ClientFilter): Promise<Client[]> {
      let query = clientFor(ctx)
        .from("clients")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (filter?.type) query = query.eq("type", filter.type);
      if (filter?.search) {
        const needle = `%${filter.search}%`;
        query = query.or(
          `last_name.ilike.${needle},company_name.ilike.${needle},email.ilike.${needle}`,
        );
      }

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toClient);
    },

    async findById(
      ctx: ExecutionContext,
      clientId: string,
    ): Promise<Omit<ClientDetail, "outstanding"> | null> {
      const db = clientFor(ctx);

      // Quatre lectures en parallèle : la RLS filtre chacune sur le tenant de
      // la session, il n'y a donc rien à passer côté appelant.
      const [client, quotes, invoices, projects] = await Promise.all([
        db.from("clients").select("*").eq("id", clientId).is("deleted_at", null).maybeSingle(),
        db
          .from("quotes")
          .select("id, quote_number, title, total_ttc, status, created_at")
          .eq("client_id", clientId)
          .order("created_at", { ascending: false }),
        db
          .from("invoices")
          .select("id, invoice_number, title, total_ttc, amount_paid, status, due_date")
          .eq("client_id", clientId)
          .order("due_date", { ascending: false }),
        db
          .from("projects")
          .select("id, name, status, planned_start_date")
          .eq("client_id", clientId)
          .is("deleted_at", null),
      ]);

      if (client.error) fail(client.error);
      if (!client.data) return null;

      return {
        client: toClient(client.data),
        quotes: (quotes.data ?? []).map((q: Row) => ({
          id: q.id,
          quoteNumber: q.quote_number ?? "",
          title: q.title,
          totalTtc: num(q.total_ttc),
          status: q.status,
          createdAt: q.created_at,
        })),
        invoices: (invoices.data ?? []).map((i: Row) => ({
          id: i.id,
          invoiceNumber: i.invoice_number ?? "",
          title: i.title,
          totalTtc: num(i.total_ttc),
          amountPaid: num(i.amount_paid),
          status: i.status,
          dueDate: i.due_date,
        })),
        projects: (projects.data ?? []).map((p: Row) => ({
          id: p.id,
          name: p.name,
          status: p.status,
          plannedStartDate: p.planned_start_date ?? null,
        })),
      };
    },

    async save(ctx: ExecutionContext, command: SaveClientCommand): Promise<Client> {
      const db = clientFor(ctx);
      const columns = toColumns(command);

      // `tenant_id` n'est jamais transmis : la policy WITH CHECK impose déjà
      // celui de la session, et l'envoyer inviterait à croire qu'on peut le
      // choisir.
      const { data, error } = command.clientId
        ? await db
            .from("clients")
            .update(columns)
            .eq("id", command.clientId)
            .select()
            .maybeSingle()
        : await db.from("clients").insert(columns).select().single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Ce client n'existe pas.", {
          clientId: command.clientId,
        });
      }
      return toClient(data);
    },

    async archive(ctx: ExecutionContext, clientId: string): Promise<void> {
      // Archivage et non suppression : devis et factures gardent une
      // référence, et un document comptable ne doit pas pointer dans le vide.
      const { error } = await clientFor(ctx)
        .from("clients")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", clientId);

      if (error) fail(error);
    },
  };
}
